import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@contexts/User.context';
import { getAnnotationSummary, getTaxonomyNodesForMode } from '@utils/api';
import { buildObjectTree } from '@utils/taxonomyTree';
import { mergeAnnotationSummary } from '@routes/userRoutes/objects/objectTree.js';
import { holdTrainingLocks } from '@stores/useTrainingLockStore';

/**
 * The caller's taxonomy for one project type: the flat `nodes` list and the same
 * nodes as a nested `tree`. Cached per mode, so every consumer shares one request.
 * The request sends only the mode — the server reads the user from the token and
 * the active org from its own state — so both go in the key, or an org switch
 * would keep serving the previous org's cached tree.
 *
 * @param {string} mode - A `PROJECT_MODE` value.
 * @param {{ withCounts?: boolean, trackCountsFetching?: boolean }} [options] - `withCounts`
 *   also loads the annotation summary (an aggregate over every node) and merges it in, for
 *   the Objects page (tree and details) and the annotation panel's tree.
 *   `trackCountsFetching` makes `countsFetching` true while counts a save just changed are
 *   read again: a refetch of an invalidated summary. Not the first load, when there are no
 *   counts to update, nor a window-focus refetch, whose counts are not known to be stale.
 *   Opt-in, since reading it re-renders the caller on every refetch.
 */
export const useObjects = (mode, { withCounts = false, trackCountsFetching = false } = {}) => {
	const { currentUser } = useAuth();
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: ['objectTaxonomy', currentUser?.uuid, currentUser?.active_org_id, { mode }],
		queryFn: () => getTaxonomyNodesForMode(mode),
		enabled: Boolean(mode),
		// The taxonomy changes rarely; without this the map refetches it on every
		// panel mount and window focus.
		staleTime: 5 * 60 * 1000,
	});

	// Separate from the tree, so it can lag or fail without hiding it; the cards and
	// details then say the counts are not available (or could not load).
	const summaryKey = ['annotationSummary', currentUser?.uuid, currentUser?.active_org_id];
	const summaryQuery = useQuery({
		queryKey: summaryKey,
		// A parent a child was just trained from stays untrainable until the server agrees.
		queryFn: async () => holdTrainingLocks(await getAnnotationSummary()),
		enabled: withCounts,
	});

	// A failed refetch over cached data is not a failure to show. A caller without counts
	// ignores a summary another page cached, so its nodes keep their identity.
	const summary = withCounts ? summaryQuery.data : undefined;
	const summaryFailed = withCounts && summaryQuery.isError && !summaryQuery.data;
	// Stable while loading, so the tree is not rebuilt on every render.
	const nodes = useMemo(
		() => mergeAnnotationSummary(query.data ?? [], summary, { failed: summaryFailed }),
		[query.data, summary, summaryFailed]
	);
	const tree = useMemo(() => buildObjectTree(nodes), [nodes]);

	// A save invalidates the summary, and the flag stays set until a refetch succeeds. It is
	// subscribed to, not read during whatever render happens next: a save during a
	// window-focus refetch already in flight leaves `isRefetching` true throughout, so no
	// prop this hook reads is guaranteed to change and re-render it.
	const trackInvalidation = withCounts && trackCountsFetching;
	const subscribeToCache = useCallback(
		onChange =>
			trackInvalidation ? queryClient.getQueryCache().subscribe(onChange) : () => {},
		[queryClient, trackInvalidation]
	);
	const summaryInvalidated = useSyncExternalStore(
		subscribeToCache,
		() => trackInvalidation && queryClient.getQueryState(summaryKey)?.isInvalidated === true
	);

	return {
		nodes,
		tree,
		isLoading: query.isLoading,
		isError: query.isError,
		countsFetching: trackInvalidation && summaryQuery.isRefetching && summaryInvalidated,
	};
};
